/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  FlatList,
  RefreshControl,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

// JOBENG Logo component using the app icon
const CompanyLogo = ({ uri, size = 32 }) => {
  if (uri) {
    return (
      <Image
        source={{ uri }}
        style={{ width: size, height: size, borderRadius: 6 }}
        resizeMode="contain"
      />
    );
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
};


const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const getH = async () => {
  const tok = await AsyncStorage.getItem('auth_token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': API_KEY, ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
};

const NotificationsScreen = ({ navigation }) => {
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    AsyncStorage.getItem('company_profile').then(raw => {
      if (!raw) return;
      try {
        const p = JSON.parse(raw);
        const logo = p.logo || p.logo_url || p.company_logo || p.banner_image_url;
        if (logo) setCompanyLogo(logo.startsWith('http') || logo.startsWith('data:') ? logo : `https://api.jobeng.ai${logo}`);
      } catch {}
    }).catch(() => {});
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [unreadCount, setUnreadCount] = useState(0);

  // Mock notifications data

  const filterOptions = [
    { id: 'All', name: 'All', icon: 'bell' },
    { id: 'Unread', name: 'Unread', icon: 'bell-outline' },
    { id: 'Campaign', name: 'Campaigns', icon: 'bullhorn' },
    { id: 'System', name: 'System', icon: 'cog' },
    { id: 'Action Required', name: 'Action Required', icon: 'alert-circle' },
  ];

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    filterNotifications();
    updateUnreadCount();
  }, [notifications, activeFilter]);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const h = await getH();
      const bid = await AsyncStorage.getItem('company_id').catch(() => null);
      const res = await fetch(
        `${API_BASE}/api/data/campaign_notifications?company_id=${parseInt(bid)||0}&limit=100`,
        { headers: h }
      );
      const json = await res.json().catch(() => ({}));
      const data = Array.isArray(json?.data) ? json.data : [];
      if (data.length) {
        const mapped = data.map(n => ({
          id:            n.id || n.notification_id,
          type:          n.notification_type || n.type || 'system_update',
          title:         n.title,
          message:       n.message || n.body || '',
          timestamp:     n.created_at || n.timestamp,
          isRead:        n.is_read || n.isRead || false,
          priority:      n.priority || 'low',
          campaignId:    n.campaign_id || n.campaignId || null,
          campaignName:  n.campaign_name || n.campaignName || null,
          actionRequired: n.action_required || n.actionRequired || false,
          icon:          n.icon || 'bell-outline',
          iconColor:     n.icon_color || n.iconColor || '#0e4e96',
        }));
        setNotifications(mapped);
        setUnreadCount(mapped.filter(n => !n.isRead).length);
        setLoading(false);
        setRefreshing(false);
        return;
      }
    } catch (e) {
      console.warn('[Notifications] load error:', e.message);
    }
    // Fallback: empty state with a welcome message
    setNotifications([]);
    setUnreadCount(0);
    setLoading(false);
    setRefreshing(false);
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    loadNotifications();
  };

  const filterNotifications = () => {
    let filtered = notifications;

    switch (activeFilter) {
      case 'Unread':
        filtered = notifications.filter(n => !n.isRead);
        break;
      case 'Campaign':
        filtered = notifications.filter(n =>
          [
            'campaign_performance',
            'campaign_status',
            'campaign_completion',
            'budget_alert',
            'asset_approval',
          ].includes(n.type)
        );
        break;
      case 'System':
        filtered = notifications.filter(n =>
          ['system_update', 'security', 'audience_insight'].includes(n.type)
        );
        break;
      case 'Action Required':
        filtered = notifications.filter(n => n.actionRequired);
        break;
      default:
        filtered = notifications;
    }

    setFilteredNotifications(filtered);
  };

  const updateUnreadCount = () => {
    const count = notifications.filter(n => !n.isRead).length;
    setUnreadCount(count);
  };

  const markAsRead = notificationId => {
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, isRead: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    getH().then(async h => {
      const bid = await AsyncStorage.getItem('company_id').catch(() => null);
      fetch(`${API_BASE}/api/data/campaign_notifications/mark-all-read`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ company_id: parseInt(bid) || 0 }),
      }).catch(() => {});
    });
  };

  const deleteNotification = notificationId => {
    Alert.alert(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            getH().then(h =>
              fetch(`${API_BASE}/api/data/campaign_notifications/${notificationId}`, {
                method: 'DELETE', headers: h,
              }).catch(() => {})
            );
          },
        },
      ]
    );
  };

  const handleNotificationPress = notification => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.campaignId) {
      navigation.navigate('CampaignDetails', { campaign: { id: notification.campaignId, name: notification.campaignName }, campaignId: notification.campaignId });
    } else if (notification.type === 'system_update') {
      navigation.navigate('NotificationSettingsCampaign');
    }
  };

  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return '#dc3545';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  const renderNotificationItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.notificationCard,
        !item.isRead && styles.unreadNotification,
      ]}
      onPress={() => handleNotificationPress(item)}
    >
      <View style={styles.notificationHeader}>
        <View style={styles.notificationIcon}>
          <MaterialCommunityIcons
            name={item.icon}
            size={24}
            color={item.iconColor}
          />
        </View>
        <View style={styles.notificationContent}>
          <View style={styles.notificationTitleRow}>
            <Text
              style={[
                styles.notificationTitle,
                !item.isRead && styles.unreadTitle,
              ]}
            >
              {item.title}
            </Text>
            <View style={styles.notificationMeta}>
              {item.actionRequired && (
                <View style={styles.actionRequiredBadge}>
                  <Text style={styles.actionRequiredText}>Action</Text>
                </View>
              )}
              <View
                style={[
                  styles.priorityIndicator,
                  { backgroundColor: getPriorityColor(item.priority) },
                ]}
              />
            </View>
          </View>
          <Text style={styles.notificationMessage}>{item.message}</Text>
          <View style={styles.notificationFooter}>
            <Text style={styles.notificationTime}>
              {formatTimestamp(item.timestamp)}
            </Text>
            {item.campaignName && (
              <Text style={styles.campaignTag}>{item.campaignName}</Text>
            )}
          </View>
        </View>
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => deleteNotification(item.id)}
        >
          <Ionicons name="close" size={20} color="#999" />
        </TouchableOpacity>
      </View>
      {!item.isRead && <View style={styles.unreadIndicator} />}
    </TouchableOpacity>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <MaterialCommunityIcons name="bell-outline" size={60} color="#ccc" />
      <Text style={styles.emptyTitle}>No notifications</Text>
      <Text style={styles.emptySubtitle}>
        {activeFilter === 'All'
          ? "You're all caught up! New notifications will appear here."
          : `No ${activeFilter.toLowerCase()} notifications found.`}
      </Text>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <CompanyLogo uri={companyLogo} size={30} />
        <TouchableOpacity
          onPress={() => navigation.navigate('NotificationSettingsCampaign')}
        >
          <Ionicons name="settings-outline" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Title and Actions */}
      <View style={styles.titleContainer}>
        <View style={styles.titleRow}>
          <Text style={styles.screenTitle}>Notifications</Text>
          {unreadCount > 0 && (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
            </View>
          )}
        </View>
        {unreadCount > 0 && (
          <TouchableOpacity
            style={styles.markAllButton}
            onPress={markAllAsRead}
          >
            <Text style={styles.markAllButtonText}>Mark all as read</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Filter Tabs */}
      <View style={styles.filterContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterScroll}
        >
          {filterOptions.map(filter => (
            <TouchableOpacity
              key={filter.id}
              style={[
                styles.filterButton,
                activeFilter === filter.id && styles.activeFilterButton,
              ]}
              onPress={() => setActiveFilter(filter.id)}
            >
              <MaterialCommunityIcons
                name={filter.icon}
                size={16}
                color={activeFilter === filter.id ? '#0e4e96' : '#666'}
              />
              <Text
                style={[
                  styles.filterText,
                  activeFilter === filter.id && styles.activeFilterText,
                ]}
              >
                {filter.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Notifications List */}
      <FlatList
        data={filteredNotifications}
        renderItem={renderNotificationItem}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContainer}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#0e4e96']}
            tintColor="#0e4e96"
          />
        }
        ListEmptyComponent={renderEmptyState}
        showsVerticalScrollIndicator={false}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleContainer: {
    backgroundColor: '#fff',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  screenTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 10,
  },
  unreadBadge: {
    backgroundColor: '#dc3545',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 24,
    alignItems: 'center',
  },
  unreadBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  markAllButton: {
    alignSelf: 'flex-start',
  },
  markAllButtonText: {
    color: '#0e4e96',
    fontSize: 14,
    fontWeight: '500',
  },
  filterContainer: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  filterScroll: {
    paddingHorizontal: 10,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    marginHorizontal: 5,
  },
  activeFilterButton: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  filterText: {
    color: '#666',
    fontWeight: '500',
    marginLeft: 5,
  },
  activeFilterText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  listContainer: {
    padding: 15,
    flexGrow: 1,
  },
  notificationCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    position: 'relative',
  },
  unreadNotification: {
    borderLeftWidth: 4,
    borderLeftColor: '#0e4e96',
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notificationIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 5,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    flex: 1,
    marginRight: 10,
  },
  unreadTitle: {
    fontWeight: 'bold',
  },
  notificationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionRequiredBadge: {
    backgroundColor: '#dc3545',
    borderRadius: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginRight: 8,
  },
  actionRequiredText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  priorityIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  notificationMessage: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 10,
  },
  notificationFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  notificationTime: {
    fontSize: 12,
    color: '#999',
  },
  campaignTag: {
    fontSize: 12,
    color: '#0e4e96',
    backgroundColor: '#e0f2ff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  deleteButton: {
    padding: 5,
    marginLeft: 10,
  },
  unreadIndicator: {
    position: 'absolute',
    top: 15,
    right: 15,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#0e4e96',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#666',
    marginTop: 15,
    marginBottom: 5,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 40,
  },
});

export default NotificationsScreen;
